import { Account } from "models/Account";
import CardHeader from "components/CardHeader";
import { Person } from "models/Person";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Link } from "react-router-dom";
import { Alert, Badge, Card, CardBody } from "reactstrap";
import * as api from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { OfficeTranslationKey } from "i18n/officeTranslations";

interface Props { account: Account; onChange: (account: Account) => void; }

const personFieldKeys: Record<"name" | "last_name" | "title" | "email" | "phone", OfficeTranslationKey> = {
    name: "common.name",
    last_name: "accounts.accountPersonsCard.lastName",
    title: "common.title",
    email: "common.email",
    phone: "common.phone",
};

const emptyDraft = () => ({ name: "", last_name: "", email: "", phone: "", title: "", birth_date: "" });

const contactValuesForComparison = ({ email, phone }: Pick<Person, "email" | "phone">) => ({
    email: email.trim().toLowerCase(),
    phone: phone.replace(/[\s().-]/g, "").replace(/^00/, "+"),
});

const findMatchingPersons = (persons: Person[], contact: Pick<Person, "email" | "phone">) => {
    const entered = contactValuesForComparison(contact);
    return persons.filter(person => {
        const existing = contactValuesForComparison(person);
        return (entered.email !== "" && entered.email === existing.email)
            || (entered.phone !== "" && entered.phone === existing.phone);
    });
};

const AccountPersonsCard: React.FC<Props> = ({ account, onChange }) => {
    const { t } = useOfficeTranslation();
    const [persons, setPersons] = useState<Person[]>([]);
    const [selectedId, setSelectedId] = useState("");
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingPersons, setLoadingPersons] = useState(true);
    const [personsLoadFailed, setPersonsLoadFailed] = useState(false);
    const [error, setError] = useState<OfficeTranslationKey | null>(null);
    const [draft, setDraft] = useState(emptyDraft);

    const loadPersons = useCallback(async () => {
        setLoadingPersons(true);
        setPersonsLoadFailed(false);
        try {
            const loaded = await api.getPersons();
            setPersons(loaded);
            return loaded;
        } catch (reason) {
            setPersonsLoadFailed(true);
            throw reason;
        } finally {
            setLoadingPersons(false);
        }
    }, []);

    const retryLoadingPersons = useCallback(() => {
        setError(null);
        loadPersons().catch(() => setError("accounts.accountPersonsCard.couldNotLoadPersons"));
    }, [loadPersons]);

    useEffect(retryLoadingPersons, [retryLoadingPersons]);
    const linkedIds = useMemo(() => new Set(account.persons.map(link => link.person_id)), [account.persons]);
    const linked = persons.filter(person => linkedIds.has(person.id));
    const available = persons.filter(person => !linkedIds.has(person.id));
    const matchingPersons = findMatchingPersons(persons, draft);
    const personOptions = available.map(person => ({
        value: person.id,
        label: [`${person.name} ${person.last_name}`.trim(), person.email, person.phone].filter(Boolean).join(" — "),
    }));

    const completeLink = async (personId: string) => {
        const updated = await api.linkAccountPerson(account.id, personId);
        onChange(updated);
        setSelectedId("");
        setDraft(emptyDraft());
        setAdding(false);
    };

    const link = async (personId: string) => {
        if (!personId || saving || linkedIds.has(personId)) return;
        setSaving(true);
        setError(null);
        try {
            await completeLink(personId);
        } catch {
            setError("accounts.accountPersonsCard.couldNotLinkPerson");
        } finally {
            setSaving(false);
        }
    };

    const create = async () => {
        if (!draft.name.trim() || saving || loadingPersons || personsLoadFailed || matchingPersons.length > 0) return;
        setSaving(true);
        setError(null);
        let failureKey: OfficeTranslationKey = "accounts.accountPersonsCard.couldNotLoadPersons";
        try {
            const latestPersons = await loadPersons();
            if (findMatchingPersons(latestPersons, draft).length > 0) return;

            failureKey = "accounts.accountPersonsCard.couldNotCreatePerson";
            const created = await api.createPerson(Object.assign(new Person(), draft));
            setPersons(current => [...current, created]);
            setSelectedId(created.id);
            setDraft(emptyDraft());

            failureKey = "accounts.accountPersonsCard.couldNotLinkPerson";
            await completeLink(created.id);
        } catch {
            setError(failureKey);
        } finally {
            setSaving(false);
        }
    };
    const unlink = (personId: string) => api.unlinkAccountPerson(account.id, personId).then(onChange);
    const setRepresentative = (personId?: string) => {
        setSaving(true);
        api.updateAccount({ ...account, representative_person_id: personId })
            .then(onChange)
            .finally(() => setSaving(false));
    };

    return <Card><CardBody>
        <CardHeader title={t("common.persons")} icon="mdi-account-multiple-outline" count={linked.length} />
        {error && <Alert color="danger">
            {t(error)}
            {personsLoadFailed && <button
                className="btn btn-sm btn-outline-danger d-block mt-2"
                disabled={loadingPersons || saving}
                onClick={retryLoadingPersons}
                type="button"
            >
                {t("accounts.accountPersonsCard.retryLoadingPersons")}
            </button>}
        </Alert>}
        {linked.map(person => {
            const isRepresentative = account.representative_person_id === person.id;
            return <div className="d-flex justify-content-between align-items-center border-bottom py-3" key={person.id}>
                <Link to={`/persons/${person.id}`} className="text-body">
                    <strong>{person.name} {person.last_name}</strong>
                    {isRepresentative && <Badge color="primary" pill className="ms-2">{t("accounts.accountPersonsCard.representative")}</Badge>}
                    <small className="d-block text-muted">{person.title || person.email}</small>
                </Link>
                <div className="d-flex gap-1">
                    <button
                        className={`btn btn-sm ${isRepresentative ? "btn-primary" : "btn-light"}`}
                        type="button"
                        disabled={saving}
                        aria-pressed={isRepresentative}
                        title={isRepresentative ? t("accounts.accountPersonsCard.removeAsRepresentative") : t("accounts.accountPersonsCard.setAsRepresentative")}
                        onClick={() => setRepresentative(isRepresentative ? undefined : person.id)}
                    >
                        <i className="mdi mdi-account-tie-outline" />
                    </button>
                    {person.email && <Link className="btn btn-sm btn-light" aria-label={t("common.compose")} to={`/conversations/compose?person=${person.id}&customer=${account.id}`}><i className="mdi mdi-email-outline" /></Link>}
                    <button className="btn btn-sm btn-light text-danger" type="button" disabled={saving} onClick={() => unlink(person.id)}><i className="mdi mdi-link-variant-off" /></button>
                </div>
            </div>;
        })}
        {!linked.length && !adding && <p className="text-muted mt-3 mb-0">{t("accounts.accountPersonsCard.noPersonsLinkedYet")}</p>}
        {adding && <div className="border rounded p-3 mt-3">
            <label className="form-label" htmlFor="existing-account-person">{t("accounts.accountPersonsCard.linkExistingPerson")}</label>
            <div className="d-flex gap-2 mb-3">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <Select
                        inputId="existing-account-person"
                        isClearable
                        isDisabled={loadingPersons || saving}
                        isLoading={loadingPersons}
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        noOptionsMessage={() => t("accounts.accountPersonsCard.noMatchingPersons")}
                        options={personOptions}
                        placeholder={t("accounts.accountPersonsCard.selectPerson")}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 1060 }) }}
                        value={personOptions.find(option => option.value === selectedId) ?? null}
                        onChange={option => setSelectedId(option?.value ?? "")}
                    />
                </div>
                <button className="btn btn-outline-primary" type="button" disabled={!selectedId || saving || loadingPersons} onClick={() => link(selectedId)}>
                    {t("common.link")}
                </button>
            </div>
            <div className="text-center text-muted mb-3">{t("common.orCreateANewPerson")}</div>
            {(["name", "last_name", "title", "email", "phone"] as const).map(field => <input
                key={field}
                type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                className="form-control mb-2"
                aria-label={t(personFieldKeys[field])}
                disabled={saving}
                placeholder={t(personFieldKeys[field])}
                value={draft[field]}
                onChange={event => setDraft(current => ({ ...current, [field]: event.target.value }))}
            />)}
            <label className="form-label" htmlFor="new-person-birth-date">{t("common.dateOfBirth")}</label>
            <input
                id="new-person-birth-date"
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                className="form-control mb-2"
                disabled={saving}
                value={draft.birth_date}
                onChange={event => setDraft(current => ({ ...current, birth_date: event.target.value }))}
            />
            {matchingPersons.length > 0 && <Alert color="warning">
                <p className="mb-2">{t("accounts.accountPersonsCard.contactAlreadyInUse")}</p>
                {matchingPersons.map(person => {
                    const fullName = `${person.name} ${person.last_name}`.trim();
                    return <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 border-top pt-2 mt-2" key={person.id}>
                        <div>
                            <Link className="text-body" to={`/persons/${person.id}`}><strong>{fullName}</strong></Link>
                            {person.title && <small className="d-block text-muted">{person.title}</small>}
                            {person.email && <small className="d-block">{person.email}</small>}
                            {person.phone && <small className="d-block">{person.phone}</small>}
                        </div>
                        {linkedIds.has(person.id)
                            ? <span className="badge bg-light text-muted">{t("accounts.accountPersonsCard.alreadyLinked")}</span>
                            : <button className="btn btn-outline-primary btn-sm" disabled={saving} onClick={() => link(person.id)} type="button">
                                {t("accounts.accountPersonsCard.linkPerson", { name: fullName })}
                            </button>
                        }
                    </div>;
                })}
            </Alert>}
            <div className="d-flex gap-2">
                <button className="btn btn-light" type="button" disabled={saving} onClick={() => setAdding(false)}>{t("common.cancel")}</button>
                <button className="btn btn-primary flex-grow-1" type="button" disabled={!draft.name.trim() || saving || loadingPersons || personsLoadFailed || matchingPersons.length > 0} onClick={create}>
                    {saving ? t("common.saving") : t("accounts.accountPersonsCard.createAndLink")}
                </button>
            </div>
        </div>}
        {!adding && <button className="btn btn-primary w-100 mt-3" type="button" onClick={() => setAdding(true)}><i className="mdi mdi-account-plus me-1" />{t("accounts.accountPersonsCard.addPerson")}</button>}
    </CardBody></Card>;
};

export default AccountPersonsCard;
